import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Syringe, X, Calendar, FileText, Save, Loader2 } from 'lucide-react';
import { Pet, Vaccination } from '../../types';
import { getPets } from '../../services/petService';
import toast from 'react-hot-toast';
import FormField from '../Form/FormField';
import { useFormValidation } from '../../hooks/useFormValidation';
import { compose, required } from '../../utils/validators';
import { todayLocal } from '../../utils/dateUtils';

interface VaccinationFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  vaccination?: Vaccination | null;
}

const COMMON_VACCINES = [
  'Karma Aşı', 'Kuduz', 'Karma Yıllık', 'İç-dış Parazit', 'Köpek Karma',
  'Kedi Karma', 'FELV', 'Bordatella',
];

const VaccinationForm: React.FC<VaccinationFormProps> = ({ isOpen, onClose, onSubmit, vaccination }) => {
  const [pets, setPets] = useState<Pet[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const form = useFormValidation(
    {
      pet_id: '',
      vaccine_name: '',
      vaccine_date: todayLocal(),
      next_due_date: '',
      next_vaccine_name: '',
      vaccine_notes: '',
    },
    {
      pet_id: (v) => required(v, 'Hasta'),
      vaccine_name: compose((v) => required(v, 'Aşı adı')),
      vaccine_date: (v) => required(v, 'Aşı tarihi'),
    }
  );

  // Gelecek tarihli (veya mevcut kaydı pending) ise "yapılmamış": sonraki doz alanı anlamsız
  const isPending = vaccination
    ? vaccination.status === 'pending'
    : !!form.values.vaccine_date && form.values.vaccine_date > todayLocal();

  useEffect(() => {
    if (isOpen) {
      getPets().then(setPets).catch(console.error);
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = previousOverflow; };
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (vaccination) {
      form.setValues({
        pet_id: vaccination.pet_id || vaccination.petId || '',
        vaccine_name: vaccination.name || '',
        vaccine_date: vaccination.date || todayLocal(),
        next_due_date: vaccination.next_due_date || vaccination.nextDueDate || '',
        next_vaccine_name: '',
        vaccine_notes: vaccination.notes || '',
      });
    } else {
      form.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vaccination, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.validateAll()) {
      toast.error('Lütfen formdaki hataları düzeltin');
      return;
    }
    if (!isPending && form.values.next_due_date && form.values.next_due_date < form.values.vaccine_date) {
      toast.error('Sonraki doz tarihi aşı tarihinden önce olamaz');
      return;
    }
    setSubmitting(true);
    try {
      const nextDue = isPending ? '' : form.values.next_due_date;
      const endOfToday = new Date();
      endOfToday.setHours(23, 59, 59, 999);
      const vaccineDateObj = new Date(form.values.vaccine_date);
      const isCompletedByDate = vaccineDateObj <= endOfToday;

      // Tek kayıt — "gelecek aşı" sadece mevcut kaydın notuna eklenir
      // Böylece çift kayıt sorunu önlenir
      const notesWithNext = !isPending && form.values.next_vaccine_name && nextDue
        ? `${form.values.vaccine_notes ? form.values.vaccine_notes + ' | ' : ''}Sonraki: ${form.values.next_vaccine_name}`
        : form.values.vaccine_notes;

      const currentVaccine = {
        pet_id: form.values.pet_id,
        name: form.values.vaccine_name.trim(),
        date: form.values.vaccine_date,
        next_due_date: nextDue || '',
        status: vaccination ? (vaccination.status as any) : (isCompletedByDate ? 'completed' : 'pending'),
        notes: notesWithNext,
      };
      await onSubmit(currentVaccine);

      onClose();
      form.reset();
    } catch (error: any) {
      toast.error('Hata: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center z-[9999] p-4" style={{backgroundColor:"rgba(0,0,0,0.75)"}}>
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 200, damping: 22 }}
        className="dribbble-panel max-w-lg w-full max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0 bg-white rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white shadow-md shadow-purple-300/40">
              <Syringe className="w-4 h-4" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">
              {vaccination ? 'Aşı Düzenle' : 'Yeni Aşı Kaydı'}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {/* Hasta seçimi */}
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-gray-700">
                Hasta <span className="text-red-500">*</span>
              </label>
              <select
                value={form.values.pet_id}
                onChange={(e) => form.setValue('pet_id', e.target.value)}
                onBlur={() => form.setFieldTouched('pet_id')}
                className={`w-full rounded-xl border-2 px-3 py-2.5 text-sm bg-white outline-none focus:ring-4 transition-all ${
                  form.touched.pet_id && form.errors.pet_id
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20 bg-red-50/30'
                    : form.values.pet_id
                    ? 'border-emerald-300 focus:border-emerald-500 focus:ring-emerald-500/20 bg-emerald-50/30'
                    : 'border-gray-200 focus:border-teal-400 focus:ring-teal-500/20'
                }`}
              >
                <option value="">Hasta seçiniz...</option>
                {pets.map(pet => (
                  <option key={pet.id} value={pet.id}>
                    {pet.name} ({pet.breed}) {pet.owner?.name ? `· ${pet.owner.name}` : ''}
                  </option>
                ))}
              </select>
              {form.touched.pet_id && form.errors.pet_id && (
                <p className="text-xs text-red-600 font-medium">{form.errors.pet_id}</p>
              )}
            </div>

            {/* Aşı adı + quick chips */}
            <FormField
              label="Aşı Adı"
              name="vaccine_name"
              icon={Syringe}
              value={form.values.vaccine_name}
              onChange={(v) => form.setValue('vaccine_name', v)}
              onBlur={() => form.setFieldTouched('vaccine_name')}
              error={form.errors.vaccine_name}
              touched={form.touched.vaccine_name}
              valid={!form.errors.vaccine_name}
              placeholder="Kuduz, Karma, vb."
              required
            />
            <div className="flex flex-wrap gap-1.5 -mt-2">
              {COMMON_VACCINES.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => form.setValue('vaccine_name', v)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                    form.values.vaccine_name === v
                      ? 'bg-purple-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-purple-100 hover:text-purple-700'
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>

            {/* Tarih */}
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-gray-700">
                Aşı Tarihi <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={form.values.vaccine_date}
                onChange={(e) => form.setValue('vaccine_date', e.target.value)}
                className="w-full rounded-xl border-2 border-gray-200 focus:border-teal-400 focus:ring-4 focus:ring-teal-500/20 outline-none px-3 py-2.5 text-sm bg-white"
              />
            </div>

            {/* Sonraki aşı tarihi (opsiyonel - tek alan); planlı kayıtta gizli */}
            {!isPending && (
            <div className="bg-amber-50/50 border border-amber-200 rounded-xl p-4 space-y-3">
              <p className="text-xs font-bold text-amber-700 flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                Sonraki Aşı Hatırlatması (Opsiyonel)
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold text-gray-700">Tarih</label>
                  <input
                    type="date"
                    value={form.values.next_due_date}
                    min={form.values.vaccine_date || undefined}
                    onChange={(e) => form.setValue('next_due_date', e.target.value)}
                    className="w-full rounded-lg border-2 border-amber-200 focus:border-amber-400 focus:ring-4 focus:ring-amber-500/20 outline-none px-3 py-2 text-sm bg-white"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold text-gray-700">Sonraki Aşı Adı</label>
                  <input
                    type="text"
                    value={form.values.next_vaccine_name}
                    onChange={(e) => form.setValue('next_vaccine_name', e.target.value)}
                    placeholder="Aynı aşı veya farklı..."
                    className="w-full rounded-lg border-2 border-amber-200 focus:border-amber-400 focus:ring-4 focus:ring-amber-500/20 outline-none px-3 py-2 text-sm bg-white"
                  />
                </div>
              </div>
              <p className="text-[11px] text-amber-700/80 italic">
                ℹ️ Bu sadece hatırlatma içindir — ayrı bir kayıt oluşturulmaz
              </p>
              {form.values.next_due_date && form.values.next_due_date < form.values.vaccine_date && (
                <p className="text-xs text-red-600 font-medium">Sonraki doz tarihi aşı tarihinden önce olamaz</p>
              )}
            </div>
            )}

            {/* Notlar */}
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-gray-700">
                Notlar <span className="text-gray-400 font-normal">(Opsiyonel)</span>
              </label>
              <textarea
                value={form.values.vaccine_notes}
                onChange={(e) => form.setValue('vaccine_notes', e.target.value)}
                rows={3}
                placeholder="Aşı ile ilgili notlar..."
                className="w-full rounded-xl border-2 border-gray-200 focus:border-teal-400 focus:ring-4 focus:ring-teal-500/20 outline-none px-3 py-2.5 text-sm bg-white resize-none"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 p-4 border-t border-gray-200 bg-white shrink-0 rounded-b-2xl">
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1" disabled={submitting}>
              İptal
            </button>
            <button type="submit" disabled={submitting || !form.isValid} className="btn btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-60">
              {submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Kaydediliyor...</>
              ) : (
                <><Save className="w-4 h-4" /> {vaccination ? 'Güncelle' : 'Kaydet'}</>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>,
    document.body
  );
};

export default VaccinationForm;
